// PowerUpDropManager.js
// Manages the dropping of power-ups from zombies

import * as THREE from 'three';

/**
 * PowerUpDropManager - Controls the dropping of power-ups from zombies
 * This is a placeholder implementation that drops colored spheres based on tier:
 * - Tier 1: White Sphere → Basic
 * - Tier 2: Blue Sphere → Intermediate
 * - Tier 3: Purple Sphere → Rare
 * - Tier 4: Gold Sphere → Ultra Rare / Boss Only
 * 
 * In future implementations, these drops will trigger actual power-up effects.
 */
export class PowerUpDropManager {
    constructor(scene) {
        this.scene = scene;
        this.drops = [];
        
        // Set up drop rates for each zombie type and tier
        this.dropRates = {
            regular: {
                tier1: 0.20, // 20% chance for white drop
                tier2: 0.05, // 5% chance for blue drop
                tier3: 0.01, // 1% chance for purple drop
                tier4: 0.00  // 0% chance for gold drop (boss only)
            },
            runner: {
                tier1: 0.30, // 30% chance for white drop
                tier2: 0.10, // 10% chance for blue drop
                tier3: 0.03, // 3% chance for purple drop
                tier4: 0.05  // 5% chance for gold drop
            },
            boss: {
                tier1: 0.60, // Increased from 50% to 60% chance for white drop
                tier2: 0.30, // 30% chance for blue drop
                tier3: 0.15, // 15% chance for purple drop
                tier4: 0.10  // 10% chance for gold drop
            }
        };
        
        // Define tier colors (emissive for better visibility)
        this.tierColors = {
            tier1: 0xffffff, // White
            tier2: 0x4444ff, // Blue
            tier3: 0xaa44ff, // Purple
            tier4: 0xffdd44  // Gold
        };
        
        // Set up wave multipliers (higher waves = better drops)
        this.waveMultipliers = {
            tier1: 1.05,  // 5% increase per wave for basic drops
            tier2: 1.10,  // 10% increase per wave for blue drops
            tier3: 1.15,  // 15% increase per wave for purple drops
            tier4: 1.20   // 20% increase per wave for gold drops
        };
        
        // Track live drops to clean them up later
        this.activePowerUps = [];
        
        // Make globally accessible
        window.powerUpDropManager = this;
        
        console.log('[PowerUpDropManager] Initialized');
    }
    
    /**
     * Show a notification message for power-up effects
     * @param {string} message - The message to display
     * @param {string} tier - The tier of the power-up
     */
    showNotification(message, tier) {
        const div = document.createElement('div');
        div.className = 'power-up-notification';
        div.textContent = message;
        div.style.cssText = `
            position: fixed;
            top: 30%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 15px 20px;
            border-radius: 8px;
            font-weight: bold;
            text-align: center;
            animation: pulse 2s ease-out;
            pointer-events: none;
            z-index: 1000;
            min-width: 250px;
            font-size: 18px;
            text-shadow: 0 0 10px ${this.tierColors[tier]};
        `;
        document.body.appendChild(div);
        
        // Remove after animation
        setTimeout(() => {
            if (div.parentNode) {
                div.parentNode.removeChild(div);
            }
        }, 3000);
    }
    
    /**
     * Attempt to drop a power-up based on zombie type and wave
     * @param {string} zombieType - Type of zombie (regular, runner, boss)
     * @param {number} wave - Current wave number
     * @param {THREE.Vector3} position - Position to drop at
     * @returns {Object|null} The created drop or null if no drop
     */
    maybeDrop(zombieType, wave, position) {
        // Normalize zombie type
        let type = zombieType;
        if (zombieType === 'base') {
            type = 'regular';
        }
        
        // Ensure we have valid drop rates for this zombie type
        if (!this.dropRates[type]) {
            console.warn(`[PowerUpDropManager] Unknown zombie type: ${zombieType}, defaulting to regular`);
            type = 'regular';
        }
        
        // Get base drop rates for this zombie type
        const baseRates = this.dropRates[type];
        
        // Scale drop rates based on wave number
        const scaledRates = {
            tier1: Math.min(1.0, baseRates.tier1 * Math.pow(this.waveMultipliers.tier1, wave - 1)),
            tier2: Math.min(1.0, baseRates.tier2 * Math.pow(this.waveMultipliers.tier2, wave - 1)),
            tier3: Math.min(1.0, baseRates.tier3 * Math.pow(this.waveMultipliers.tier3, wave - 1)),
            tier4: Math.min(1.0, baseRates.tier4 * Math.pow(this.waveMultipliers.tier4, wave - 1))
        };
        
        console.log(`[PowerUpDropManager] Checking drops for ${type} zombie (wave ${wave})`);
        console.log(`[PowerUpDropManager] Drop rates:`, scaledRates);
        
        // Check for tier 4 (gold) drop first - rarest
        if (Math.random() < scaledRates.tier4) {
            return this.createDrop('tier4', position);
        }
        
        // Check for tier 3 (purple) drop
        if (Math.random() < scaledRates.tier3) {
            return this.createDrop('tier3', position);
        }
        
        // Check for tier 2 (blue) drop
        if (Math.random() < scaledRates.tier2) {
            return this.createDrop('tier2', position);
        }
        
        // Check for tier 1 (white) drop - most common
        if (Math.random() < scaledRates.tier1) {
            return this.createDrop('tier1', position);
        }
        
        // No drop
        return null;
    }
    
    /**
     * Create a power-up drop at the specified position
     * @param {string} tier - Tier of drop (tier1, tier2, tier3, tier4)
     * @param {THREE.Vector3} position - Position to create drop at
     * @returns {Object} The created drop
     */
    createDrop(tier, position) {
        // Create a small sphere for the drop
        const radius = 0.3; // Small sphere
        const geometry = new THREE.SphereGeometry(radius, 16, 16);
        
        // Create material based on tier with glow effect
        const material = new THREE.MeshStandardMaterial({
            color: this.tierColors[tier],
            emissive: this.tierColors[tier],
            emissiveIntensity: 0.7,
            metalness: 0.8,
            roughness: 0.2
        });
        
        // Create mesh
        const dropMesh = new THREE.Mesh(geometry, material);
        
        // Position slightly above ground to be visible
        dropMesh.position.copy(position);
        dropMesh.position.y += 1.0; // Increased from 0.5 to 1.0 for better visibility
        
        // Add to scene
        this.scene.add(dropMesh);
        
        // Add properties to track this drop
        dropMesh.tier = tier;
        dropMesh.dropTime = performance.now();
        dropMesh.lifetime = 20000; // Increased from 15000 to 20000 (20 seconds)
        
        // Store in active powerups array
        this.activePowerUps.push(dropMesh);
        
        // Create pickup animation
        this.animateDrop(dropMesh);
        
        console.log(`[PowerUpDropManager] Created ${tier} drop at ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`);
        
        return dropMesh;
    }
    
    /**
     * Animate a power-up drop (rotation and hover effect)
     * @param {Object} dropMesh - The drop mesh to animate
     */
    animateDrop(dropMesh) {
        const startY = dropMesh.position.y;
        const startTime = performance.now();
        
        // Animation function
        const animate = () => {
            const now = performance.now();
            const elapsed = now - startTime;
            
            // Check if drop should be removed due to timeout
            if (now - dropMesh.dropTime > dropMesh.lifetime) {
                this.removeDrop(dropMesh);
                return;
            }
            
            // Rotate the drop
            dropMesh.rotation.y += 0.05;
            
            // Bob up and down with increased height
            const hoverOffset = Math.sin(elapsed / 300) * 0.3; // Increased from 0.1 to 0.3 for more noticeable floating
            dropMesh.position.y = startY + hoverOffset;
            
            // Pulse the emissive intensity
            const baseMaterial = dropMesh.material;
            baseMaterial.emissiveIntensity = 0.5 + Math.sin(elapsed / 200) * 0.3;
            
            // Continue animation if drop still exists
            if (this.activePowerUps.includes(dropMesh)) {
                requestAnimationFrame(animate);
            }
        };
        
        // Start animation
        requestAnimationFrame(animate);
    }
    
    /**
     * Remove a power-up drop
     * @param {Object} dropMesh - The drop mesh to remove
     */
    removeDrop(dropMesh) {
        const index = this.activePowerUps.indexOf(dropMesh);
        if (index !== -1) {
            this.activePowerUps.splice(index, 1);
            this.scene.remove(dropMesh);
            
            // Dispose of geometry and material
            if (dropMesh.geometry) dropMesh.geometry.dispose();
            if (dropMesh.material) dropMesh.material.dispose();
        }
    }
    
    /**
     * Pickup a power-up (called when player collides with drop)
     * @param {Object} dropMesh - The drop mesh being picked up
     */
    pickupDrop(dropMesh) {
        const tier = dropMesh.tier;
        
        // Apply power-up effects based on tier
        switch(tier) {
            case 'tier1':
                // Add 25 health
                if (window.playerController) {
                    window.playerController.addHealth(25);
                    this.showNotification('+25 Health!', tier);
                }
                break;
                
            case 'tier2':
                // Add 40 health
                if (window.playerController) {
                    window.playerController.addHealth(40);
                    this.showNotification('+40 Health!', tier);
                }
                break;
                
            case 'tier3':
                // Full health
                if (window.playerController) {
                    window.playerController.setFullHealth();
                    this.showNotification('Full Health Restored!', tier);
                }
                break;
                
            case 'tier4':
                // Kill all zombies and advance wave
                if (window.zombieManager) {
                    // Kill all zombies
                    window.zombieManager.zombies.forEach(zombie => {
                        window.zombieManager.killZombie(zombie);
                    });
                    
                    // Advance wave
                    if (window.waveManager) {
                        window.waveManager.completeWave();
                    }
                    
                    this.showNotification('Wave Cleared!', tier);
                }
                break;
        }
        
        // Create short pickup effect
        this.createPickupEffect(dropMesh.position.clone(), tier);
        
        // Remove the drop
        this.removeDrop(dropMesh);
        
        return tier;
    }
    
    /**
     * Create a visual effect when a power-up is picked up
     * @param {THREE.Vector3} position - Position to create effect at
     * @param {string} tier - Tier of the power-up
     */
    createPickupEffect(position, tier) {
        // Create particles for pickup effect
        const particleCount = 15;
        const color = this.tierColors[tier];
        
        for (let i = 0; i < particleCount; i++) {
            // Create small sphere for particle
            const radius = 0.1;
            const geometry = new THREE.SphereGeometry(radius, 8, 8);
            const material = new THREE.MeshBasicMaterial({
                color: color,
                transparent: true,
                opacity: 1.0
            });
            
            const particle = new THREE.Mesh(geometry, material);
            
            // Position at power-up location
            particle.position.copy(position);
            
            // Add random velocity for explosion effect
            particle.velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 0.1,
                Math.random() * 0.1 + 0.05,
                (Math.random() - 0.5) * 0.1
            );
            
            // Add to scene
            this.scene.add(particle);
            
            // Track for cleanup
            this.activePowerUps.push(particle);
            
            // Animate particle
            const startTime = performance.now();
            const lifetime = 800; // 800ms particle effect
            
            const animateParticle = () => {
                const elapsed = performance.now() - startTime;
                const progress = elapsed / lifetime;
                
                if (progress >= 1) {
                    // Remove particle when animation completes
                    this.removeDrop(particle);
                    return;
                }
                
                // Move particle based on velocity
                particle.position.x += particle.velocity.x;
                particle.position.y += particle.velocity.y;
                particle.position.z += particle.velocity.z;
                
                // Apply gravity to y velocity
                particle.velocity.y -= 0.002;
                
                // Fade out
                particle.material.opacity = 1 - progress;
                
                // Continue animation
                requestAnimationFrame(animateParticle);
            };
            
            // Start animation
            requestAnimationFrame(animateParticle);
        }
    }
    
    /**
     * Update method to be called each frame
     * Checks for player collisions with drops
     * @param {Object} playerMesh - The player mesh to check for collisions
     */
    update(playerMesh) {
        if (!playerMesh) return;
        
        // Check for collisions with active power-ups
        for (let i = this.activePowerUps.length - 1; i >= 0; i--) {
            const drop = this.activePowerUps[i];
            
            // Skip particles (they have no tier)
            if (!drop.tier) continue;
            
            // Calculate distance to player
            const distance = playerMesh.position.distanceTo(drop.position);
            
            // If player is close enough, pick up the power-up
            if (distance < 1.5) {
                this.pickupDrop(drop);
            }
        }
    }
} 